import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../features/user/userSlice';
import styles from './SuspireCircle.module.css';
import FeatureHeader from '../FeatureHeader';
import PostCard from './PostCard';
import PostDetail from './PostDetail';
import CreatePostModal from './CreatePostModal';
import TopBrands from './TopBrands';
import { showToast } from '../../utils/toast';
import {
  fetchFeed,
  fetchPost,
  fetchPostingEligibility,
  fetchMySubmissions,
  fetchPendingPosts,
  uploadAttachment,
  submitPost,
  approvePost,
  rejectPost,
  adminCreatePost,
  adminUpdatePost,
  normalizePost,
  reactToPost,
} from '../../api/circleAPI';

/* Solid white padlock, 16:23 per the Figma. The keyhole is punched out to the
   button's orange rather than painted, so it reads as a cut-out. */
function WhiteLockIcon() {
  return (
    <svg width="12" height="17" viewBox="0 0 16 23" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M4 9.5V5.6a4 4 0 0 1 8 0V9.5" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" />
      <rect x="1" y="9.2" width="14" height="12.8" rx="2.6" fill="#ffffff" />
      <circle cx="8" cy="14.6" r="1.6" fill="#f26522" />
      <rect x="7.25" y="15.3" width="1.5" height="3.5" rx="0.75" fill="#f26522" />
    </svg>
  );
}

/* Upgrade-modal badge, per the Figma: a light ring with sparkles either side,
   holding an amber padlock whose keyhole is punched out in black. */
function UpgradeLockBadge() {
  return (
    <div className={styles.upgradeIcon}>
      <span className={styles.upgradeSparkle} aria-hidden="true">✦</span>
      <span className={styles.upgradeLockRing}>
        <svg width="36" height="36" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M8 10V7a4 4 0 0 1 8 0v3"
            fill="none"
            stroke="#E0A030"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <rect x="4.75" y="10" width="14.5" height="10.5" rx="2.4" fill="#F5B335" />
          <circle cx="12" cy="14.6" r="1.55" fill="#111827" />
          <rect x="11.25" y="15.2" width="1.5" height="3.1" rx="0.75" fill="#111827" />
        </svg>
      </span>
      <span className={styles.upgradeSparkle} aria-hidden="true">✦</span>
    </div>
  );
}

export const POST_TYPES = {
  announcement: { label: 'Announcement',        color: '#3b82f6' },
  opportunity:  { label: 'Opportunity',          color: '#10b981' },
  knowledge:    { label: 'Knowledge / Learning', color: '#8b5cf6' },
  promotion:    { label: 'Promotion / Feature',  color: '#f59e0b' },
  resource:     { label: 'Resource / Document',  color: '#64748b' },
};

export default function SuspireCircle() {
  const { user_type, token } = useSelector(state => state.user);
  const isAdmin = user_type !== 'USER' && user_type !== null;
  const dispatch = useDispatch();

  const handle401 = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  // Feed state
  const [posts, setPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextPage, setNextPage] = useState(2);
  const [loadingMore, setLoadingMore] = useState(false);

  // Admin pending posts
  const [pendingPosts, setPendingPosts] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  // Brand-specific state
  const [isEligible, setIsEligible] = useState(false);
  const [myPosts, setMyPosts] = useState([]);

  // UI state
  const [activeTab, setActiveTab] = useState('feed');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [upgradeContext, setUpgradeContext] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  // The ?post=<id> the page was opened with, consumed once. Read during the
  // first render so a fast feed response can't land before we've looked.
  const deepLinkRef = useRef(
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('post')
  );

  // ── Load feed ────────────────────────────────────────────────────────────
  const loadFeed = useCallback(() => {
    setFeedLoading(true);
    setFeedError(null);
    fetchFeed(token, { page: 1 })
      .then(data => {
        setPosts(data.results);
        setHasMore(data.hasMore);
        setNextPage(2);
      })
      .catch(err => {
        if (err.message && err.message.includes('401')) {
          handle401();
        } else {
          setFeedError(err.message);
        }
      })
      .finally(() => setFeedLoading(false));
  }, [token, handle401]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // ── Admin: pending posts ─────────────────────────────────────────────────
  const loadPendingPosts = useCallback(() => {
    if (!isAdmin) return;
    setPendingLoading(true);
    fetchPendingPosts(token)
      .then(data => setPendingPosts(data.results))
      .catch(err => {
        if (err.message && err.message.includes('401')) handle401();
      })
      .finally(() => setPendingLoading(false));
  }, [token, isAdmin, handle401]);

  useEffect(() => { if (isAdmin) loadPendingPosts(); }, [loadPendingPosts, isAdmin]);

  // ── Brand: eligibility + my submissions ──────────────────────────────────
  const refreshMyPosts = useCallback(() => {
    if (isAdmin) return;
    fetchMySubmissions(token)
      .then(data => setMyPosts(data.results))
      .catch(() => {});
  }, [token, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    fetchPostingEligibility(token)
      .then(data => setIsEligible(data.eligible))
      // Deliberately leaves eligibility as it was. Setting it false told a
      // customer who may well be entitled to post that they are not, on the
      // strength of a question we failed to ask — and swapped their compose box
      // for an upgrade prompt.
      .catch(() => {});
    refreshMyPosts();
  }, [token, isAdmin, refreshMyPosts]);

  // ── Pagination ───────────────────────────────────────────────────────────
  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchFeed(token, { page: nextPage })
      .then(data => {
        setPosts(prev => [...prev, ...data.results]);
        setHasMore(data.hasMore);
        setNextPage(prev => prev + 1);
      })
      .catch(err => showToast({ type: 'error', message: err.message }))
      .finally(() => setLoadingMore(false));
  };

  // ── Post actions ─────────────────────────────────────────────────────────
  /* The address bar tracks whichever post is open, so the page can be linked to
     and refreshed. replaceState rather than pushState — opening a post is not a
     navigation, and Back should leave Circle, not close an overlay. */
  const syncPostParam = useCallback((postId) => {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    if (postId) url.searchParams.set('post', postId);
    else url.searchParams.delete('post');
    window.history.replaceState({}, '', url);
  }, []);

  const openPost = useCallback((post) => {
    setSelectedPost(post);
    syncPostParam(post?.id);
  }, [syncPostParam]);

  const closePost = useCallback(() => {
    setSelectedPost(null);
    syncPostParam(null);
  }, [syncPostParam]);

  const handlePostClick = (post) => {
    if (post.visibility_gated && !isAdmin) {
      setUpgradeContext({ plan: post.gated_plan_name });
    } else {
      openPost(post);
    }
  };

  /* ?post=<id> — where the "View on Suspire Circle" button in the notification
     email lands. The post may sit several pages deep in the feed, so it is
     fetched by id rather than looked up in whatever is already loaded. */
  useEffect(() => {
    const postId = deepLinkRef.current;
    if (!postId) return;
    deepLinkRef.current = null;   // one shot; never re-opens on a later render

    let cancelled = false;
    fetchPost(token, postId)
      .then(post => {
        if (cancelled) return;
        if (post.visibility_gated && !isAdmin) {
          setUpgradeContext({ plan: post.gated_plan_name });
          syncPostParam(null);
        } else {
          setSelectedPost(post);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Deleted, rejected, or not visible to this user. Say so once and leave
        // them on the feed rather than on an empty overlay.
        showToast({ type: 'error', message: 'That post is no longer available.' });
        syncPostParam(null);
      });

    return () => { cancelled = true; };
  }, [token, isAdmin, syncPostParam]);

  const handleReact = useCallback(async (postId, reactionType) => {
    // Optimistic update so the UI feels instant
    const optimistic = (post) => {
      if (post.id !== postId) return post;
      const prev = post.user_reaction;
      const counts = { ...post.reactions };
      if (prev) counts[prev] = Math.max(0, (counts[prev] || 0) - 1);
      if (prev !== reactionType) {
        counts[reactionType] = (counts[reactionType] || 0) + 1;
        return { ...post, reactions: counts, user_reaction: reactionType };
      }
      return { ...post, reactions: counts, user_reaction: null };
    };
    setPosts(prev => prev.map(optimistic));
    setMyPosts(prev => prev.map(optimistic));
    setSelectedPost(prev => (prev && prev.id === postId ? optimistic(prev) : prev));

    try {
      const data = await reactToPost(token, postId, reactionType);
      // Reconcile with server-confirmed counts
      const fromServer = (post) => {
        if (post.id !== postId) return post;
        return { ...post, reactions: data.reactions, user_reaction: data.user_reaction };
      };
      setPosts(prev => prev.map(fromServer));
      setMyPosts(prev => prev.map(fromServer));
      setSelectedPost(prev => (prev && prev.id === postId ? fromServer(prev) : prev));
    } catch {
      // On failure reload the feed to get accurate counts
      loadFeed();
    }
  }, [token, loadFeed]);

  // ── Admin: approve ───────────────────────────────────────────────────────
  const handleApprove = async (postId) => {
    try {
      await approvePost(token, postId);
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
      showToast({ type: 'success', message: 'Post approved!' });
      loadFeed();
    } catch (err) {
      showToast({ type: 'error', message: err.message || 'Failed to approve post' });
    }
  };

  // ── Admin: reject ────────────────────────────────────────────────────────
  const handleReject = async (postId, reason) => {
    try {
      await rejectPost(token, postId, reason);
      setPendingPosts(prev => prev.filter(p => p.id !== postId));
      showToast({ type: 'success', message: 'Post rejected.' });
    } catch (err) {
      showToast({ type: 'error', message: err.message || 'Failed to reject post' });
    }
  };

  // ── Admin: edit pending post ─────────────────────────────────────────────
  const handleEdit = (post) => {
    setEditingPost(post);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingPost(null);
  };

  // ── Create / edit post ───────────────────────────────────────────────────
  const handleCreatePost = async (formData) => {
    const { signal } = formData;

    // Upload the attachment (if any) and return the fields the post needs.
    // `attachment_name` carries the uploader's original filename so the feed can
    // show "Brand_Deck.pdf" instead of the uuid-prefixed storage key.
    const uploadIfNeeded = async () => {
      if (!formData.attachment) return {};
      const { url, filename } = await uploadAttachment(token, formData.attachment, { signal });
      return {
        attachment_url: url,
        attachment_name: filename,
        attachment_type: formData.attachment.type.startsWith('image/') ? 'IMAGE' : 'PDF',
      };
    };

    // A cancel the user asked for is not an error — say nothing and close.
    const isAborted = (err) => err?.aborted || err?.name === 'RequestAbortedError';

    // ── Admin editing a pending brand post ──────────────────────────────────
    if (editingPost) {
      try {
        const updated = await adminUpdatePost(token, editingPost.id, {
          title:            formData.title,
          body:             formData.body,
          body_format:      formData.body_format || 'HTML',
          post_type:        formData.post_type.toUpperCase(),
          cta_url:          formData.cta_url || null,
          cta_label:        formData.cta_label || null,
          visibility_scope: formData.plan_access === 'all' ? 'ALL' : 'PLANS',
          visible_to_plans: formData.plan_access === 'selected' ? formData.plan_selection : [],
        }, { signal });
        setPendingPosts(prev => prev.map(p => p.id !== editingPost.id ? p : normalizePost(updated)));
        closeModal();
        showToast({ type: 'success', message: 'Post updated!' });
      } catch (err) {
        if (isAborted(err)) return;
        showToast({ type: 'error', message: err.message || 'Failed to update post' });
      }
      return;
    }

    // ── Admin creating a new post ──────────────────────────────────────────
    if (isAdmin) {
      try {
        const attachment = await uploadIfNeeded();

        const payload = {
          title: formData.title,
          body: formData.body,
          body_format: formData.body_format || 'HTML',
          post_type: formData.post_type.toUpperCase(),
          ...attachment,
          ...(formData.cta_url ? { cta_url: formData.cta_url, cta_label: formData.cta_label || 'Learn More' } : {}),
          visibility_scope: formData.plan_access === 'all' ? 'ALL' : 'PLANS',
          visible_to_plans: formData.plan_access === 'selected' ? formData.plan_selection : [],
          send_email_notification: formData.send_email,
          client_request_id: formData.client_request_id,
        };

        const created = await adminCreatePost(token, payload, { signal });
        closeModal();
        showToast({
          type: 'success',
          message: created?._deduplicated
            ? 'This post was already published — showing the existing post.'
            : formData.send_email
              ? 'Post published! Notification emails are being sent in the background.'
              : 'Post published!',
        });
        loadFeed();
      } catch (err) {
        if (isAborted(err)) {
          // The request was cancelled mid-flight, so we can't know whether the
          // server committed it. Refresh so the feed shows the truth.
          loadFeed();
          return;
        }
        showToast({ type: 'error', message: err.message || 'Failed to create post' });
      }
      return;
    }

    // ── Brand: submit to backend API ───────────────────────────────────────
    try {
      const attachment = await uploadIfNeeded();

      const payload = {
        title: formData.title,
        body: formData.body,
        body_format: formData.body_format || 'HTML',
        post_type: formData.post_type.toUpperCase(),
        ...attachment,
        ...(formData.cta_url ? { cta_url: formData.cta_url, cta_label: formData.cta_label || 'Learn More' } : {}),
        client_request_id: formData.client_request_id,
      };

      const submitted = await submitPost(token, payload, { signal });
      closeModal();
      showToast({
        type: 'success',
        message: submitted?._deduplicated
          ? 'This post was already submitted — showing the existing submission.'
          : 'Post submitted for review!',
      });
      refreshMyPosts();
    } catch (err) {
      if (isAborted(err)) {
        refreshMyPosts();
        return;
      }
      showToast({ type: 'error', message: err.message || 'Failed to submit post' });
    }
  };

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const pendingCount      = myPosts.filter(p => p.status === 'pending').length;
  const rejectedCount     = myPosts.filter(p => p.status === 'rejected').length;
  const adminPendingCount = pendingPosts.length;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerBrand}>
            <div>
              <FeatureHeader
                src="/images/feature-headers/suspire-circle.png"
                alt="Suspire Circle"
                style={{ marginBottom: '6px' }}
              />
              <p className={styles.subtitle}>
                Curated insights, opportunities, and stories from the Suspire ecosystem
              </p>
            </div>
          </div>

          <div className={styles.headerActions}>
            {isAdmin ? (
              <button
                className={styles.createBtn}
                onClick={() => setShowCreateModal(true)}
              >
                + Create Post
              </button>
            ) : (
              isEligible ? (
                <button
                  className={styles.createBtn}
                  onClick={() => setShowCreateModal(true)}
                >
                  + Create Post
                </button>
              ) : (
                <button
                  className={`${styles.createBtn} ${styles.lockedBtn}`}
                  onClick={() => setUpgradeContext('create')}
                >
                  <WhiteLockIcon />
                  Upgrade to Create Post
                </button>
              )
            )}
          </div>
        </div>

        {/* ── Content row: main column + Top Brands sidebar ── */}
        <div className={styles.contentRow}>
          <div className={styles.mainCol}>

        {/* ── Tabs ── */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'feed' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('feed')}
          >
            Feed
          </button>


          {isAdmin && (
            <button
              className={`${styles.tab} ${activeTab === 'pending' ? styles.tabActive : ''}`}
              onClick={() => { setActiveTab('pending'); loadPendingPosts(); }}
            >
              Pending
              {adminPendingCount > 0 && (
                <span className={styles.tabBadge}>{adminPendingCount}</span>
              )}
            </button>
          )}

          {!isAdmin && (
            <button
              className={`${styles.tab} ${activeTab === 'myposts' ? styles.tabActive : ''}`}
              onClick={() => { setActiveTab('myposts'); refreshMyPosts(); }}
            >
              My Posts
              {pendingCount > 0 && (
                <span className={styles.tabBadge}>{pendingCount}</span>
              )}
              {rejectedCount > 0 && (
                <span className={`${styles.tabBadge} ${styles.tabBadgeRejected}`}>{rejectedCount} rejected</span>
              )}
            </button>
          )}
        </div>

        {/* ── Feed tab ── */}
        {activeTab === 'feed' && (
          <div className={styles.feed}>
            {feedLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner} />
                <p>Loading posts…</p>
              </div>
            ) : feedError ? (
              <div className={styles.errorState}>
                <p>Failed to load posts: {feedError}</p>
                <button className={styles.retryBtn} onClick={loadFeed}>Retry</button>
              </div>
            ) : posts.length === 0 ? (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>◎</span>
                <p>No posts yet. Be the first to share!</p>
              </div>
            ) : (
              <>
                {posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    postTypes={POST_TYPES}
                    onPostClick={handlePostClick}
                    onReact={handleReact}
                    isAdmin={isAdmin}
                  />
                ))}
                {hasMore && (
                  <div className={styles.loadMoreRow}>
                    <button
                      className={styles.loadMoreBtn}
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading…' : 'Load more posts'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}


        {/* ── Pending tab (admin only) ── */}
        {activeTab === 'pending' && isAdmin && (
          <div className={styles.feed}>
            {pendingLoading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner} />
                <p>Loading pending posts…</p>
              </div>
            ) : pendingPosts.length === 0 ? (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>✓</span>
                <p>No pending posts to review.</p>
              </div>
            ) : (
              pendingPosts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  postTypes={POST_TYPES}
                  onPostClick={handlePostClick}
                  onReact={handleReact}
                  isAdmin={isAdmin}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEdit={handleEdit}
                />
              ))
            )}
          </div>
        )}

        {/* ── My Posts tab (brand only) ── */}
        {activeTab === 'myposts' && !isAdmin && (
          <div className={styles.feed}>
            {myPosts.length === 0 ? (
              <div className={styles.empty}>
                <span className={styles.emptyIcon}>📝</span>
                <p>You have not submitted any posts yet.</p>
              </div>
            ) : (
              myPosts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  postTypes={POST_TYPES}
                  onPostClick={handlePostClick}
                  onReact={handleReact}
                />
              ))
            )}
          </div>
        )}

          </div>{/* .mainCol */}

          <TopBrands />
        </div>{/* .contentRow */}
      </div>

      {/* ── Post detail overlay ── */}
      {selectedPost && (
        <PostDetail
          post={selectedPost}
          postTypes={POST_TYPES}
          onClose={closePost}
          onReact={handleReact}
          isAdmin={isAdmin}
        />
      )}

      {/* ── Create / Edit Post Modal ── */}
      {showCreateModal && (
        <CreatePostModal
          isOpen={showCreateModal}
          onClose={closeModal}
          onSubmit={handleCreatePost}
          isAdmin={isAdmin}
          postTypes={POST_TYPES}
          editPost={editingPost}
        />
      )}

      {/* ── Upgrade prompt ── */}
      {upgradeContext && (
        <div className={styles.overlay} onClick={() => setUpgradeContext(null)}>
          <div className={styles.upgradeModal} onClick={e => e.stopPropagation()}>
            <UpgradeLockBadge />
            {upgradeContext === 'create' ? (
              <>
                <h3>Upgrade to Post in Circle</h3>
                <p>
                  Posting in Suspire Circle is available on Root, Bloom, and Thrive plans.
                  Upgrade your plan to share your brand's story with the community.
                </p>
              </>
            ) : (
              <>
                <h3>This post requires {upgradeContext.plan} plan or above</h3>
                <p>
                  Upgrade your plan to unlock full access to this post and other
                  premium content in Suspire Circle.
                </p>
              </>
            )}
            <div className={styles.upgradeActions}>
              <button
                className={styles.upgradePlanBtn}
                onClick={() => {
                  setUpgradeContext(null);
                  window.location.href = "/subscriptions";
                }}
              >
                Upgrade Plan
              </button>
              <button
                className={styles.upgradeCancelBtn}
                onClick={() => setUpgradeContext(null)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
